import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';
import HomeHero from './HomeHero/HomeHero';
import HomeService from './HomeService/HomeService';
import HomeProcess from './HomeProcess/HomeProcess';
import introductionImg from '../../assets/Home/intro.jpg';

const Home = () => {
  // Real-time Scroll Reveal Animation Hook
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('home-page-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const animatedElements = document.querySelectorAll(
      '.home-page-reveal, .home-page-reveal-left, .home-page-reveal-right, .home-page-reveal-scale'
    );
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      num: "01",
      tag: "Ecosystem Alignment",
      title: "Integrated Institutional Perspective",
      desc: "Aligning academic infrastructure, hospital capabilities, clinical flow, and documentation into a single synchronized ecosystem.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      )
    },
    {
      num: "02",
      tag: "Full Lifecycle",
      title: "Establishment to Expansion Scope",
      desc: "Comprehensive advisory supporting greenfield new medical college setups through undergraduate and postgraduate seat additions.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    },
    {
      num: "03",
      tag: "Dual Advantage",
      title: "Advisory + Faculty Execution",
      desc: "Combining strategic medical college planning with active, verified permanent & full-time faculty recruitment.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      )
    },
    {
      num: "04",
      tag: "Methodical Model",
      title: "Structured Execution Framework",
      desc: "Milestone-driven project roadmaps with verifiable checklists, gap remediation, and statutory audit readiness.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      )
    },
    {
      num: "05",
      tag: "Tailored Strategy",
      title: "Institution-Specific Engagement",
      desc: "Custom-tailored advisory aligned with each promoter's clinical strengths, target MBBS intake, and bed capacity.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      )
    },
    {
      num: "06",
      tag: "Strict NDA Ethics",
      title: "Confidential & Transparent Ethics",
      desc: "Strict non-disclosure protocols protecting institutional strategies, promoter data, and verified faculty credentials.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      )
    }
  ];

  const leftPillars = pillars.slice(0, 3);
  const rightPillars = pillars.slice(3, 6);

  return (
    <>
      <Helmet>
        <title>Medical College Establishment Advisory | Nutrivio</title>
        <meta
          name="description"
          content="Nutrivio helps medical institutions with new medical college establishment, MBBS seat expansion, PG course expansion, compliance, documentation readiness, and faculty recruitment."
        />
        <meta
          name="keywords"
          content="medical college establishment, medical college advisory, MBBS seat expansion, PG course expansion, institutional compliance, documentation readiness, medical faculty recruitment"
        />
      </Helmet>

      <div className="home-page-wrapper">
      
      {/* 1. HERO COMPONENT */}
      <HomeHero />

      {/* 2. STATS / HIGHLIGHTS BAR */}
      <section className="home-page-stats-bar home-page-reveal">
        <div className="home-page-container">
          <div className="home-page-stats-grid">
            <div className="home-page-stat-col">
              <div className="home-page-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
                </svg>
              </div>
              <h4>Greenfield to Expansion</h4>
              <p>End-to-end setup advisory from concept to operational readiness.</p>
              <span className="home-page-stat-tag">Advisory Scope</span>
            </div>

            <div className="home-page-stat-col">
              <div className="home-page-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </div>
              <h4>Teaching Hospital Synergy</h4>
              <p>Clinical material, bed capacity, ICU/OT, and occupancy optimization.</p>
              <span className="home-page-stat-tag">Clinical Sync</span>
            </div>

            <div className="home-page-stat-col">
              <div className="home-page-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h4>Academic Faculty Network</h4>
              <p>Permanent & full-time candidate screening across all medical disciplines.</p>
              <span className="home-page-stat-tag">100% Verified</span>
            </div>

            <div className="home-page-stat-col">
              <div className="home-page-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <h4>Pan-India Scope</h4>
              <p>Structured support for trusts, societies, universities, and healthcare groups.</p>
              <span className="home-page-stat-tag">National Reach</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 3. REDESIGNED INTRODUCTION SECTION ================= */}
      <section className="home-page-intro">
        <div className="home-page-container">
          
          {/* Centered Top Heading */}
          <div className="home-page-header-center home-page-reveal">
            <div className="home-page-section-eyebrow center">
              <span className="home-page-eyebrow-line"></span>
              <span>Building the Foundations of Medical Education</span>
              <span className="home-page-eyebrow-line"></span>
            </div>
            <h2 className="home-page-section-title single-line-title">
              Integrated Professional Support for Medical Colleges &amp; Teaching Hospitals
            </h2>
            <p className="home-page-section-desc full-width-desc">
              Establishing, expanding or strengthening a Medical College requires coordinated planning across academic infrastructure, Teaching Hospital capabilities, faculty and resident manpower, clinical material, institutional documentation and overall preparedness.
            </p>
          </div>

          {/* Asymmetrical Showcase Grid */}
          <div className="home-page-intro-showcase-grid">
            
            {/* Left Column: Visual Ecosystem Card with Embedded Metrics */}
            <div className="home-page-intro-left-card home-page-reveal-left">
              <div className="home-page-intro-media-frame">
                <img 
                  src={introductionImg} 
                  alt="Teaching Hospital Operations" 
                  className="home-page-intro-media-img"
                />
                <div className="home-page-intro-media-overlay"></div>

                {/* Floating Top Tag */}
                <div className="home-page-intro-top-tag">
                  <span className="home-page-intro-pulse-dot"></span>
                  <span>Integrated Academic & Clinical Platform</span>
                </div>

                {/* Bottom Embedded Metric Badges */}
                <div className="home-page-intro-bottom-chips">
                  <div className="home-page-intro-chip">
                    <strong>Hospital Bed Ratios</strong>
                    <span>OPD/IPD & ICU Statistics</span>
                  </div>
                  <div className="home-page-intro-chip">
                    <strong>Faculty Deployment</strong>
                    <span>Verified Academic Manpower</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: 2 Structured Executive Insight Blocks */}
            <div className="home-page-intro-right-blocks home-page-reveal-right">
              
              {/* Insight Block 1 */}
              <div className="home-page-intro-block">
                <div className="home-page-intro-block-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                </div>
                <div>
                  <h4>Coordinated Planning Across Multi-Dimensional Needs</h4>
                  <p>
                    Nutrivio Medical Services Private Limited provides an integrated professional platform designed to support institutions across these critical areas.
                  </p>
                </div>
              </div>

              {/* Insight Block 2 */}
              <div className="home-page-intro-block">
                <div className="home-page-intro-block-icon green">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <div>
                  <h4>Structured Assessment &amp; Readiness Execution</h4>
                  <p>
                    Our approach begins with understanding the institution&apos;s objectives and existing capabilities, followed by structured assessment, planning, documentation coordination, gap identification and institutional readiness support according to the agreed scope of engagement.
                  </p>
                </div>
              </div>

              {/* 4 Core Focus Checkmark Pills */}
              <div className="home-page-intro-check-grid">
                <div className="home-page-intro-check-item">
                  <span className="home-page-check-icon">✓</span>
                  <span>Academic Infrastructure & Labs</span>
                </div>
                <div className="home-page-intro-check-item">
                  <span className="home-page-check-icon">✓</span>
                  <span>Clinical Material & Bed Ratios</span>
                </div>
                <div className="home-page-intro-check-item">
                  <span className="home-page-check-icon">✓</span>
                  <span>Faculty & Resident Deployment</span>
                </div>
                <div className="home-page-intro-check-item">
                  <span className="home-page-check-icon">✓</span>
                  <span>Statutory Document Verification</span>
                </div>
              </div>

              {/* Bottom Quote Ribbon */}
              <div className="home-page-intro-quote-strip">
                <span>From a proposed New Medical College to the expansion of an established institution, Nutrivio provides structured support throughout all development stages.</span>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 4. SERVICES COMPONENT */}
      <div className="home-page-reveal">
        <HomeService />
      </div>

      {/* 5. PROCESS COMPONENT */}
      <div className="home-page-reveal">
        <HomeProcess />
      </div>

      {/* ================= 6. "WHY US" 3D QUESTION MARK SHOWCASE ================= */}
      <section className="home-page-why">
        <div className="home-page-container">
          
          <div className="home-page-header-center home-page-reveal">
            <div className="home-page-section-eyebrow center">
              <span className="home-page-eyebrow-line"></span>
              <span>Strategic Advantage</span>
              <span className="home-page-eyebrow-line"></span>
            </div>
            <h2 className="home-page-section-title single-line-title">
              Why Medical Promoters Partner with Nutrivio
            </h2>
            <p className="home-page-section-desc full-width-desc">
              A specialized institutional advisory model engineered to eliminate regulatory bottlenecks, synchronize hospital operations, and build verified academic manpower.
            </p>
          </div>

          {/* 3-Column Layout: Left 3 | Center 3D Mark | Right 3 */}
          <div className="home-page-why-showcase-layout">
            
            {/* LEFT 3 PILLARS */}
            <div className="home-page-why-column left home-page-reveal-left">
              {leftPillars.map((item, idx) => (
                <div key={idx} className="home-page-why-card">
                  <div className="home-page-why-card-header">
                    <div className="home-page-why-icon-box">{item.icon}</div>
                    <div className="home-page-why-badge-group">
                      <span className="home-page-why-num">{item.num}</span>
                      <span className="home-page-why-tag">{item.tag}</span>
                    </div>
                  </div>
                  <h3 className="home-page-why-title">{item.title}</h3>
                  <p className="home-page-why-desc">{item.desc}</p>
                  <div className="home-page-why-hover-bar"></div>
                </div>
              ))}
            </div>

            {/* CENTER PURE 3D QUESTION MARK (DESKTOP ONLY) */}
            <div className="home-page-why-3d-stage home-page-reveal-scale">
              <div className="home-page-3d-q-sculpture">
                
                {/* 3D Question Mark Glyph */}
                <div className="home-page-3d-q-char">
                  <span className="q-shadow-layer layer-4">?</span>
                  <span className="q-shadow-layer layer-3">?</span>
                  <span className="q-shadow-layer layer-2">?</span>
                  <span className="q-shadow-layer layer-1">?</span>
                  <span className="q-main-front">?</span>
                </div>

                {/* Floating Depth Ring & Base Glow */}
                <div className="home-page-3d-orbit-ring"></div>
                <div className="home-page-3d-base-shadow"></div>
                
              </div>
            </div>

            {/* RIGHT 3 PILLARS */}
            <div className="home-page-why-column right home-page-reveal-right">
              {rightPillars.map((item, idx) => (
                <div key={idx} className="home-page-why-card">
                  <div className="home-page-why-card-header">
                    <div className="home-page-why-icon-box">{item.icon}</div>
                    <div className="home-page-why-badge-group">
                      <span className="home-page-why-num">{item.num}</span>
                      <span className="home-page-why-tag">{item.tag}</span>
                    </div>
                  </div>
                  <h3 className="home-page-why-title">{item.title}</h3>
                  <p className="home-page-why-desc">{item.desc}</p>
                  <div className="home-page-why-hover-bar"></div>
                </div>
              ))}
            </div>

          </div>

        </div>
      </section>

      {/* ================= 7. COMPACT & PUNCHY EXECUTIVE CTA SECTION ================= */}
      <section className="home-page-cta home-page-reveal">
        <div className="home-page-container">
          
          <div className="home-page-cta-compact-card">
            
            {/* Ambient Radial Accent */}
            <div className="home-page-cta-ambient-glow"></div>

            {/* Header Badge */}
            <div className="home-page-cta-pill">
              <span className="home-page-cta-dot"></span>
              <span>Ready to Advance Your Medical Institution?</span>
            </div>

            {/* Compact Headline */}
            <h2 className="home-page-cta-title single-line-title">
              Planning a New Medical College? <span className="home-page-cta-gradient-text">Let us understand your project.</span>
            </h2>

            {/* Concise Full-Width Value Subtitle */}
            <p className="home-page-cta-desc full-width-desc">
              Whether establishing a New Medical College, expanding MBBS/PG seats, strengthening institutional readiness, or recruiting verified full-time faculty, Nutrivio provides structured advisory aligned with your institutional milestones.
            </p>

            {/* Action Buttons with Expanding Hover Animations */}
            <div className="home-page-cta-btn-row">
              <Link to="/for-institutions" className="home-page-cta-btn-primary">
                <span>Discuss Your Project</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>

              <a 
                href="https://wa.me/919038320103" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="home-page-cta-btn-whatsapp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
                <span>Priority WhatsApp Desk</span>
              </a>
            </div>

            {/* Refined Inline Brand Closing Motto */}
            <div className="home-page-brand-closing">
              <div className="home-page-closing-motto">From Vision to Institution. From Establishment to Expansion.</div>
              <div className="home-page-closing-company">NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED</div>
              <div className="home-page-closing-sub">Strategic Professional Support for Medical Education Institutions across India</div>
            </div>

          </div>

        </div>
      </section>

    </div>
    </>
  );
};

export default Home;